<template>
    <div class="app-content-builder-create-page">
        <div v-if="newPage"
            class="app-modal-dialog app-modal-dialog--create-page">
            <div class="app-modal-dialog__content">
                <div class="app-modal-dialog__header">
                    <h3 class="app-modal-dialog__title">Add Screen</h3>
                </div>
                <div class="app-modal-dialog__body grid">
                    <div class="col-12 col-sm-6 col-md-8">
                        <div class="input text">
                            <label for="new-screen-name">Name</label>
                            <input v-model="pageTitle"
                                id="new-screen-name"
                                ref="screenName"
                                type="text"
                                :placeholder="getDefaultTitle()"
                                autofocus>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-4">
                        <div class="radio-group">
                            <label class="radio-group__label">Source</label>
                            <div class="radio">
                                <div class="radio__input">
                                    <input v-model="createFromSource"
                                        type="radio"
                                        id="screen-source-new"
                                        name="screen-source"
                                        :value="false">
                                    </div>
                                <label for="screen-source-new">New Screen</label>
                            </div>
                            <div class="radio">
                                <div class="radio__input">
                                    <input v-model="createFromSource"
                                        type="radio"
                                        id="screen-source-import"
                                        name="screen-source"
                                        :value="true">
                                </div>
                                <label for="screen-source-import">Import</label>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6">
                        <label class="select2-label">Screen Type</label>
                        <app-select v-show="createFromSource"
                            key="create-from-source"
                            placeholder="Search for a screen..."
                            :options="remoteSourceOptions"
                            v-model="remoteSource">
                        </app-select>
                        <app-select v-show="!createFromSource"
                            key="create-from-new"
                            :groups="screenTypeChoices"
                            @update:modelValue="onScreenTypeChange"
                            placeholder="Select screen type..."
                            v-model="interaction">
                        </app-select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <div v-if="!createFromSource && hasTemplateOptions">
                            <label class="select2-label" for="new-screen-template">Screen Template Selection</label>
                            <app-select
                                key="create-from-template"
                                :choices="screenTemplateChoices"
                                v-model="currentScreenTemplate">
                            </app-select>
                        <p class="inline-message">These templates only apply to the specified screen type.</p>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-4">
                        <div class="input text">
                            <label for="new-screen-id">Internal ID (optional)</label>
                            <input v-model="pageName"
                                id="new-screen-id"
                                ref="screenId"
                                type="text"
                                :placeholder="getDefaultName('xxxx')">
                        </div>
                    </div>

                    <div v-if="powerModeEnabled" class="col-12">
                        <p class="inline-message is--warning">You are running a Lite-enabled project that only supports specific screen types. Non-supported screen types may break when exported to PPT or Word.</p>
                    </div>
                </div>

                <div class="app-modal-dialog__footer" role="group">
                    <button
                        @click="clear()"
                        class="element-button--secondary element-button">
                        Cancel
                    </button>
                    <button
                        @click="addPage()"
                        class="element-button--primary element-button"
                        :disabled="!newPageReady">
                        Submit
                    </button>
                </div>
            </div>
        </div>
        <button
            @click="createPage"
            class="element-button--secondary element-button">
            Add Screen
        </button>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters, mapMutations } from 'vuex'
import { cloneDataObject, getInteraction, getInteractionId } from '../helpers/builder'
import api from '../api/builder'
import { uuid } from '../helpers/util'

export default {
    props: { },
    data () {
        return {
            newPage: false,
            pageTitle: null,
            pageName: null,
            interaction: null,
            screenTemplateChoices: null,
            currentScreenTemplate: null,
            remoteSource: null,
            createFromSource: false
        }
    },
    computed: {
        ...mapState('builder', {
            powerModeEnabled: state => state.powerModeEnabled,
            details: state => state.details,
            selectedId: state => state.selectedId,
            source: state => state.source,
            project: state => state.project,
            dataObjects: state => state.dataObjects,
            status: state => state.status,
            outline: state => state.outline,
            screenTemplateMode: state => state.screenTemplateMode,
            remoteSources: state => state.remoteSources
        }),
        ...mapGetters('builder', [
            'dirty',
            'changes',
            'screenTypes',
            'screenTemplateForType',
            'schemaRefs',
            'getClientLODataObjects'
        ]),
        hasRemoteSource () {
            return this.createFromSource && this.remoteSource
        },
        hasTemplateOptions () {
            return this.screenTemplateMode && this.screenTemplateChoices && (this.screenTemplateChoices.length > 1 || this.getClientLODataObjects)
        },
        newPageReady () {
            return this.hasRemoteSource || this.interaction
        },
        screenTypeChoices () {
            return _.map(this.screenTypes, (types, category) => ({
                id: category,
                text: category.toUpperCase(),
                children: _.map(types, (screen, index) => ({
                    id: `${category}-${index}`,
                    text: screen.name,
                    value: screen,
                    customProperties: { category, type: screen.type }
                }))
            }))
        },
        remoteSourceOptions () {
            return {
                ajax: {
                    url: `/projects/${this.project.id}/learning/data`,
                    dataType: 'json',
                    delay: 250,
                    data: (params) => ({
                        query: params.term, // search term
                        page: params.page,
                        limit: 30
                    }),
                    processResults: this.processResults,
                    cache: true
                },
                minimumInputLength: 2
            }
        }
    },
    methods: {
        getDefaultTitle () {
            if (this.hasRemoteSource) {
                return this.remoteSource.title
            } else if (this.interaction) {
                const layout = this.interaction.layout || this.interaction.type
                return `Untitled - ${_.startCase(layout)}`
            }
            return 'Untitled'
        },
        getDefaultName (id) {
            let type = 'screen'
            if (this.hasRemoteSource) {
                type = getInteraction(this.remoteSource).type
            } else if (this.interaction) {
                type = this.interaction.type
            }

            id = id || uuid(4)
            return `${_.kebabCase(type)}-${id}` 
        },
        onScreenTypeChange (interaction) {
            const type = getInteractionId(interaction)
            // get screenTemplates for type
            const templates = this.screenTemplateForType(type)
            this.screenTemplateChoices = templates
            if (this.screenTemplateMode && !this.getClientLODataObjects) {
                this.currentScreenTemplate = this.screenTemplateChoices?.[0]?.value ?? null
            }
        },
        async createPage () {
            this.newPage = true

            // reset variables
            this.pageTitle = null
            this.pageName = null

            // set focus on input
            await this.$nextTick()
            this.$refs.screenName?.focus()
        },
        clear () {
            this.newPage = false
            this.interaction = null
            this.remoteSource = null
            this.screenTemplateChoices = null
            this.currentScreenTemplate = null
        },
        async addPage () {
            const dataObject = {
                title: this.pageTitle || this.getDefaultTitle(),
                name: this.pageName || this.getDefaultName()
            }

            if (this.createFromSource) {
                this.$store.commit('builder/setStatus', 'loading')
                const remoteObject = await api.getDataObject(`/learning/${this.remoteSource.model_id}`, this.remoteSource.id)
                _.assign(dataObject, cloneDataObject(remoteObject, ['id', 'title', 'name', 'sort_order']))
                this.$store.commit('builder/clearStatus')
            } else {
                dataObject.type = getInteractionId(this.interaction)
                dataObject.schema_ref = this.interaction.$ref
                dataObject.data = {};
                if (this.currentScreenTemplate)
                    dataObject.data = this.currentScreenTemplate.data
            }

            if (!dataObject.type) {
                alert('Invalid screen type.')
                return
            }

            // fallback schema ref
            if (!dataObject.schema_ref) {
                dataObject.schema_ref = dataObject.type
            }

            this.clear()

            this.$store.dispatch('builder/addPage', dataObject)
        },
        processResults ({ data }, { page = 1 }) {
            const groupedData = _.groupBy(data.results.data, 'model_id')
            const groupedResults = _.reduce(data.learning, (result, { id, name }) => {
                if (groupedData[id]) {
                    result.push({
                        text: name,
                        children: _.map(groupedData[id], (object) => ({
                            id: object.id,
                            text: object.title,
                            value: object
                        }))
                    })
                }
                return result
            }, [])

            return {
                results: _.concat([{ }], groupedResults),
                pagination: {
                    more: (page * data.results.per_page) < data.results.total
                }
            }
        }
    }
}
</script>
